import { getLoginUuidFromToken } from '@openeducation/oe-it-services';
import { useCallback, useReducer } from 'react';

type State = {
  error: string;
  token?: string | null;
  loginUuid?: string | null;
  id?: number | string;
  isAuthenticateSuccess?: boolean | null;
  isLoginSuccess?: boolean | null;
  tokenLoadedFromStorage: boolean;
  isLoading: boolean;
};

export enum ReducerActionType {
  STORAGE_LOAD_INIT,
  STORAGE_LOAD_COMPLETE,
  AUTHENTICATE_SUCCESS,
  AUTHORIZE_SUCCESS,
  LOGIN_FAILS,
  LOGIN_SUCCESS,
  LOGOUT,
}

type LoginInitiAction = {
  type: ReducerActionType.STORAGE_LOAD_INIT;
};

type StorageLoadedPayload = {
  token: string | null;
  loginUuid?: string | null;
};

type StorageLoadedAction = {
  type: ReducerActionType.STORAGE_LOAD_COMPLETE;
  payload: StorageLoadedPayload;
};

type AuthenticateSuccessPayload = {
  token: string;
  id: number | string;
  loginUuid?: string | null;
};

type AuthenticateSuccessAction = {
  type: ReducerActionType.AUTHENTICATE_SUCCESS;
  payload: AuthenticateSuccessPayload;
};

type LoginSuccessAction = {
  type: ReducerActionType.LOGIN_SUCCESS;
};

type LoginFailsAction = {
  type: ReducerActionType.LOGIN_FAILS;
  payload: string;
};

type LogoutAction = {
  type: ReducerActionType.LOGOUT;
};

export type ReducerAction =
  | LoginInitiAction
  | StorageLoadedAction
  | AuthenticateSuccessAction
  | LoginSuccessAction
  | LoginFailsAction
  | LogoutAction;

function loginReducer(state: State, action: ReducerAction): State {
  switch (action.type) {
    case ReducerActionType.STORAGE_LOAD_INIT:
      return {
        ...initialState,
        tokenLoadedFromStorage: true,
      };

    case ReducerActionType.STORAGE_LOAD_COMPLETE:
      // eslint-disable-next-line no-case-declarations
      const token = action.payload.token;
      return {
        ...initialState,
        token,
        tokenLoadedFromStorage: token !== null,
        isAuthenticateSuccess: token === null ? null : undefined,
        isLoginSuccess: token === null ? null : undefined,
        loginUuid: action.payload.loginUuid,
      };

    case ReducerActionType.AUTHENTICATE_SUCCESS:
      return {
        ...state,
        error: '',
        isLoading: true,
        isAuthenticateSuccess: true,
        token: action.payload.token,
        id: action.payload.id,
        loginUuid: action.payload.loginUuid,
      };

    case ReducerActionType.LOGIN_SUCCESS:
      return {
        ...state,
        error: '',
        isLoginSuccess: true,
        isLoading: false,
        tokenLoadedFromStorage: false,
      };

    case ReducerActionType.LOGIN_FAILS:
      return {
        ...state,
        isLoginSuccess: false,
        isLoading: false,
        error: action.payload as string,
      };

    case ReducerActionType.LOGOUT:
      return {
        ...initialState,
        token: null,
        isAuthenticateSuccess: false,
        isLoginSuccess: false,
      };

    default:
      return state;
  }
}

const initialState: State = {
  error: '',
  tokenLoadedFromStorage: false,
  isLoading: false,
};

const useLoginReducer = () => {
  const [state, dispatch] = useReducer(loginReducer, {
    ...initialState,
  });

  const storageLoadInit = useCallback(() => {
    dispatch({
      type: ReducerActionType.STORAGE_LOAD_INIT,
    });
  }, []);

  const storageLoadComplete = useCallback((token?: string) => {
    dispatch({
      type: ReducerActionType.STORAGE_LOAD_COMPLETE,
      payload: {
        token: token || null,
        loginUuid: getLoginUuidFromToken(token),
      },
    });
  }, []);

  const authenticateSuccess = useCallback(
    (token: string, id: number | string) => {
      dispatch({
        type: ReducerActionType.AUTHENTICATE_SUCCESS,
        payload: {
          token,
          id,
          loginUuid: getLoginUuidFromToken(token),
        },
      });
    },
    []
  );

  const loginSuccess = useCallback(() => {
    dispatch({
      type: ReducerActionType.LOGIN_SUCCESS,
    });
  }, []);

  const loginFails = useCallback((error: string) => {
    dispatch({
      type: ReducerActionType.LOGIN_FAILS,
      payload: error,
    });
  }, []);

  const logout = useCallback(() => {
    dispatch({
      type: ReducerActionType.LOGOUT,
    });
  }, []);

  const logoutAsync = useCallback(async () => {
    return new Promise((resolve) => {
      setTimeout(() => {
        dispatch({
          type: ReducerActionType.LOGOUT,
        });
        resolve(null);
      }, 500);
    });
  }, []);

  return {
    state,
    storageLoadInit,
    storageLoadComplete,
    authenticateSuccess,
    loginSuccess,
    loginFails,
    logout,
    logoutAsync,
  };
};

export default useLoginReducer;
